import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Carousel, { CarouselItem } from 'tbk-components/src/components/Carousel';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import ResourceCard, { IResourceCardProps } from '../ResourceCard';
import AnimatedArcs from '../AnimatedArcs';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Icon from 'tbk-components/src/components/Icon';
import { IResourceCardsi18n } from '../ResourceCardListing';

export interface IResourceTablesi18n {
	nameColumnLabel?: string;
	frequencyBandColumnLabel?: string;
	technologyColumnLabel?: string;
	gainColumnLabel?: string;
	weightColumnLabel?: string;
	mountColumnLabel?: string;
	notAvailableLabel?: string;
	comparisonLimitAlert?: string;
}

export interface IResourceTableListingProps extends IBaseElementProps {
	tablei18n?: IResourceTablesi18n;
	resources?: IResourceCardProps[];
	/**
	 * @noUI
	 */
	productCompareLink?: ILink;
	enableProductCompare?: boolean;
	resourceCardsi18n?: IResourceCardsi18n;
}

/**
 * Resource Listing
 * @icon slides
 * @block
 */
const ResourceTableListing: React.FC<IResourceTableListingProps> = ({
	resources = [],
	tablei18n,
	id,
	className,
	classNames = [],
	enableProductCompare = false,
	productCompareLink,
	resourceCardsi18n,
}) => {
	//Product Comparison Section
	const p = localStorage.getItem('productToCompare');
	const parsedStorage = JSON.parse(p);
	const [productsToCompare, setProducts] = useState([]);
	const [isIndexPage, setIsIndexPage] = useState(false);
	const [expandedProductBar, setExpandedProductBar] = useState(false);
	const [productComparisonLimitAlert, setProductComparisonLimitAlert] =
		useState(false);

	const barRef = useRef(null);

	useLayoutEffect(() => {
		// Check if the page is the index page by fetching the index node.
		// This node only exists on the Index Page so it is a good way to catch this.
		// As of right now, this feature only works on GNSS products.
		const indexPageNode = document.querySelector('.gnss-products');
		if (indexPageNode) {
			//Set the boolean so we can use it conditionally later
			setIsIndexPage(true);
			//Enable the locally stored product comparison
			setProducts(parsedStorage || []);
		}
	}, []);

	const productCompare = (product, isStored = false) => {
		if (productsToCompare.includes(product) || isStored) {
			if (productComparisonLimitAlert) {
				setProductComparisonLimitAlert(false);
			}
			if (isStored) {
				productsToCompare.splice(
					productsToCompare.findIndex(
						(p) => p.post_name === product.post_name,
					),
					1,
				);
			}

			setProducts(productsToCompare.filter((p) => p !== product));
			if (isIndexPage) {
				localStorage.setItem(
					'productToCompare',
					JSON.stringify(
						productsToCompare.filter((p) => p !== product),
					),
				);
			}
		} else {
			if (productsToCompare.length <= 2) {
				if (productComparisonLimitAlert) {
					setProductComparisonLimitAlert(false);
				}
				setProducts([...productsToCompare, product]);
				if (isIndexPage) {
					localStorage.setItem(
						'productToCompare',
						JSON.stringify([...productsToCompare, product]),
					);
				}
			} else {
				if (!productComparisonLimitAlert) {
					setProductComparisonLimitAlert(true);
				}
			}
		}
	};

	const submitComparison = () => {
		if (productCompareLink?.href) {
			window.location.href =
				productCompareLink?.href +
				'?products=' +
				productsToCompare.map((product) => product.ID).join('%2C');
		}
	};

	/**
	 * Hide product floating bar on single product page when compare bar is activated
	 */
	const hideFloatingBar = () => {
		const productFloatingBar = document.querySelector(
			'.floating-cta-bar',
		) as HTMLElement;

		if (!productFloatingBar) return;

		productFloatingBar.style.display =
			expandedProductBar && productsToCompare.length > 0
				? 'none'
				: 'block';
	};

	useEffect(() => {
		hideFloatingBar();

		if (expandedProductBar === true) return;

		const productFloatingBar = document.querySelector(
			'.floating-cta-bar > div',
		) as HTMLElement;

		if (!productFloatingBar || !barRef.current) return;

		if (window.innerWidth < 1024) {
			barRef.current.style.bottom =
				productFloatingBar.offsetHeight + 'px';
		} else {
			barRef.current.style.bottom = '0';
		}
	}, [productsToCompare, expandedProductBar]);
	return (
		<div
			id={id}
			className={cn(
				'resource-card-listing',
				className || ['py-8 px-3 px-xl-0', 'position-relative'],
				...classNames,
			)}
		>
			<div className="resource-card-listing__container z-1 position-relative mw-1240px w-100 p-0 mx-auto">
				<div className={cn('resource-card-listing__cards-table')}>
					<div className="resource-card-listing__table-row resource-card-table__header">
						<div className="resource-card-table__label resource-card-table__label--image"></div>
						<div className="resource-card-table__label resource-card-table__label--name">
							{tablei18n?.nameColumnLabel || 'Name'}
						</div>
						<div className="resource-card-table__label resource-card-table__label--frequency-band">
							{tablei18n?.frequencyBandColumnLabel ||
								'Frequency Band'}
						</div>
						<div className="resource-card-table__label resource-card-table__label--technology">
							{tablei18n?.technologyColumnLabel || 'Technology'}
						</div>
						<div className="resource-card-table__label resource-card-table__label--gain">
							{tablei18n?.gainColumnLabel || 'Gain'}
						</div>
						<div className="resource-card-table__label resource-card-table__label--weight">
							{tablei18n?.weightColumnLabel || 'Weight'}
						</div>
						<div className="resource-card-table__label resource-card-table__label--mount">
							{tablei18n?.mountColumnLabel || 'Mount'}
						</div>
					</div>

					<>
						{resources?.map((resource, index) => {
							const isStored = parsedStorage?.some(
								//@ts-ignore
								(storedResource) =>
									storedResource.post_name ===
									//@ts-ignore
									resource.post_name,
							);
							return (
								<div
									className="resource-card-listing__table-row"
									key={index}
								>
									<div className="resource-card-table__info resource-card-table__info--image">
										<a href={resource?.cta?.href || '#'}>
											<Image
												className="img-fluid"
												{...resource.image}
											/>
										</a>
										{enableProductCompare && (
											<div className="compare-checkbox mt-5 d-flex align-items-center gap-2">
												<span className="-translate-y-[1px] text-sm">
													{resourceCardsi18n?.cardCompareLabel ||
														'Compare'}
												</span>
												<input
													type="checkbox"
													checked={
														productsToCompare.includes(
															resource,
														) || isStored
													}
													onChange={() => {
														productCompare(
															resource,
															isStored,
														);
													}}
													aria-label={
														resourceCardsi18n?.cardCompareLabel ||
														'Compare'
													}
												/>
											</div>
										)}
									</div>
									<div className="resource-card-table__info resource-card-table__info--name">
										<a href={resource?.cta?.href || '#'}>
											<h3>{resource.heading}</h3>
										</a>
									</div>
									<div className="resource-card-table__info resource-card-table__info--frequency-band">
										<h4 className="resource-card-table__info--label">
											{tablei18n?.frequencyBandColumnLabel ||
												'Frequency Band'}
										</h4>
										{resource?.extra_features?.coverage
											?.length > 0 ? (
											<ul>
												{resource?.extra_features?.coverage.map(
													(coverage, index) => (
														<li key={index}>
															{coverage}
														</li>
													),
												)}
											</ul>
										) : (
											<p className="text-gray-700 opacity-50">
												{tablei18n?.notAvailableLabel ||
													'N/A'}
											</p>
										)}
									</div>

									<div className="resource-card-table__info resource-card-table__info--technology">
										<h4 className="resource-card-table__info--label">
											{tablei18n?.gainColumnLabel ||
												'Technology'}
										</h4>
										{resource?.extra_features?.technologies
											?.length > 0 ? (
											<p>
												{resource?.extra_features?.technologies.join(
													', ',
												)}
											</p>
										) : (
											<p className="text-gray-700 opacity-50">
												{tablei18n?.notAvailableLabel ||
													'N/A'}
											</p>
										)}
									</div>
									<div className="resource-card-table__info resource-card-table__info--gain">
										<h4 className="resource-card-table__info--label">
											{tablei18n?.technologyColumnLabel ||
												'Gain'}
										</h4>
										{resource?.extra_features?.gain
											?.length > 0 ? (
											resource.extra_features?.gain.reduce(
												(acc, gain, index) =>
													acc +
													(index ? ', ' : '') +
													gain.gain,
												'',
											)
										) : (
											<p className="text-gray-700 opacity-50">
												{tablei18n?.notAvailableLabel ||
													'N/A'}
											</p>
										)}
									</div>
									<div className="resource-card-table__info resource-card-table__info--weight">
										<h4 className="resource-card-table__info--label">
											{tablei18n?.weightColumnLabel ||
												'Weight'}
										</h4>
										{resource?.extra_features?.weight ? (
											resource?.extra_features?.weight
										) : (
											<p className="text-gray-700 opacity-50">
												{tablei18n?.notAvailableLabel ||
													'N/A'}
											</p>
										)}
									</div>
									<div className="resource-card-table__info resource-card-table__info--mount">
										<h4 className="resource-card-table__info--label">
											{tablei18n?.mountColumnLabel ||
												'Mount'}
										</h4>
										{resource?.extra_features?.mount_types
											?.length > 0 ? (
											resource?.extra_features?.mount_types.reduce(
												(acc, mount, index) =>
													acc +
													(index ? ', ' : '') +
													mount.name,
												'',
											)
										) : (
											<p className="text-gray-700 opacity-50">
												{tablei18n?.notAvailableLabel ||
													'N/A'}
											</p>
										)}
									</div>
								</div>
							);
						})}
					</>
				</div>
			</div>
			{productComparisonLimitAlert && (
				<div className="product-compare-alert bottom-0 end-0 position-fixed z-2 w-100">
					<div className="product-compare-alert__container">
						<div></div>
						<p className="text-white mb-0">
							{tablei18n?.comparisonLimitAlert ||
								'You can only compare up to 3 products.'}
						</p>
						<button
							onClick={() => {
								setProductComparisonLimitAlert(false);
							}}
						>
							<Icon name="close" />
						</button>
					</div>
				</div>
			)}
			{productsToCompare.length > 0 && (
				<div
					className={cn(
						'product-compare-bar position-fixed bottom-0 end-0 z-3 d-flex flex-column w-100',
						/**
						 * This makes sure the bar is at the bottom of the screen when expanded especially when there's a product floating bar.
						 */
						expandedProductBar && '!bottom-0',
					)}
					ref={barRef}
				>
					<button
						onClick={() => {
							setExpandedProductBar(!expandedProductBar);
							if (productComparisonLimitAlert) {
								setProductComparisonLimitAlert(false);
							}
						}}
						className="product-compare-toggle border-0 align-items-center d-flex gap-2 mx-auto me-md-0 rounded-top bg-primary px-3 py-2 text-white"
					>
						{!expandedProductBar
							? resourceCardsi18n?.viewComparisonLabel ||
							  'View Comparison '
							: resourceCardsi18n?.hideComparisonLabel ||
							  'Hide Comparison '}
						{productsToCompare.length > 0 &&
							`(${productsToCompare.length})`}
						<Icon
							name={
								!expandedProductBar
									? 'chevron-up'
									: 'chevron-down'
							}
						/>
					</button>
					{expandedProductBar && (
						<div className="product-compare-bar-expanded overflow-auto bg-light p-3">
							<div className="product-bar-head mb-3 d-flex flex-column flex-wrap align-items-center gap-2 flex-md-row-reverse">
								<Button
									size="sm"
									type="primary"
									icon="chevron-right"
									classNames={['border-0']}
									onClick={() => {
										submitComparison();
									}}
								>
									{resourceCardsi18n?.compareButtonLabel ||
										'Compare Products'}
								</Button>
								<p className="mb-0 text-muted small">
									{resourceCardsi18n?.compareLabel ||
										'Compare up to 3 products'}
								</p>
							</div>
							<div
								className="product-bar-listing d-grid justify-content-center gap-3 flex-md-wrap d-md-flex"
								style={{
									gridTemplateColumns: `repeat(${
										productsToCompare?.length == 1
											? '2'
											: productsToCompare?.length
									}, 1fr)`,
								}}
							>
								{productsToCompare.map((product, index) => (
									<button
										key={index}
										className="product-compare-preview bg-transparent border-0 text-decoration-none text-dark d-inline-flex flex-column align-items-center gap-2 flex-md-row"
										onClick={() => {
											productCompare(product);
										}}
									>
										{product.image && (
											<div className="position-relative">
												<div className="close-icon-indicator position-absolute top-0 end-0 bg-tertiary p-1 rounded-circle text-primary">
													<Icon name="close" />
												</div>
												<Image
													className="img-fluid w-100 rounded"
													{...product.image}
												/>
											</div>
										)}
										{product.heading && (
											<h4 className="mb-0 h6 text-truncate w-100">
												{product.heading}
											</h4>
										)}
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
export default ResourceTableListing;
